import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { isSupabaseEnabled } from "../lib/supabase";
import { createSeedData } from "../data/seedData";
import { loadAllFromSupabase, syncToSupabase } from "../lib/supabaseData";
import { subscribeToChanges, unsubscribeAll } from "../lib/supabaseRealtime";

export const DataContext = createContext();

export function useData() {
  return useContext(DataContext);
}

const DATA_VERSION = 27;
const SAVE_DEBOUNCE_MS = 1200;

// Cache seed data at module level — createSeedData() is expensive and was called up to 10 times
const SEED_DATA = createSeedData();

// Reverse map: supabase table name → JS camelCase key
const TABLE_TO_KEY = {
  companies: "companies", users: "users", clients: "clients", projects: "projects",
  warehouses: "warehouses", items: "items", vendors: "vendors",
  employees: "employees", boq_items: "boqItems", tasks: "tasks",
  purchase_orders: "purchaseOrders", po_line_items: "poLineItems",
  material_deliveries: "materialDeliveries", vendor_invoices: "vendorInvoices",
  vendor_payments: "vendorPayments", client_invoices: "clientInvoices",
  client_payments: "clientPayments", variations: "variations",
  warehouse_transactions: "warehouseTransactions", daily_logs: "dailyLogs",
  equipment: "equipment", todos: "todos", notifications: "notifications",
  user_notes: "userNotes", company_accounts: "companyAccounts",
  account_transactions: "accountTransactions",
  documents: "documents", drawings: "drawings", rfis: "rfis",
  submittals: "submittals", correspondence: "correspondence",
  site_photos: "sitePhotos",
};

// Primary key lookup
const PK_MAP = {
  companies: "company_id", users: "user_id", clients: "client_id", projects: "project_id",
  warehouses: "warehouse_id", items: "item_id", vendors: "vendor_id",
  employees: "employee_id", boqItems: "boq_id", tasks: "task_id",
  purchaseOrders: "po_id", poLineItems: "line_id", vendorInvoices: "invoice_id",
  vendorPayments: "payment_id", warehouseTransactions: "txn_id",
  dailyLogs: "log_id", equipment: "equipment_id", todos: "id",
  notifications: "id", userNotes: "note_id",
  companyAccounts: "account_id", accountTransactions: "txn_id",
  documents: "doc_id", drawings: "drawing_id", rfis: "rfi_id",
  submittals: "submittal_id", correspondence: "correspondence_id",
  sitePhotos: "photo_id",
  quotations: "quotation_id",
  datasheets: "datasheet_id",
  otherDocs: "other_doc_id",
  workPermitDocs: "doc_id",
  qcReportDocs: "doc_id",
};

export function DataProvider({ children }) {
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [data, setData] = useState(() => {
    try {
      const saved = localStorage.getItem("ametal_pcs_data");
      const savedVer = Number(localStorage.getItem("ametal_pcs_version") || "0");
      if (saved && savedVer >= DATA_VERSION) {
        const parsed = JSON.parse(saved);
        const seed = SEED_DATA;
        const merged = { ...seed };
        Object.keys(seed).forEach(key => {
          if (parsed[key] !== undefined) merged[key] = parsed[key];
        });
        // Migration: if this browser has saved data from before the user_data_saved
        // flag existed, detect "real user data" (non-empty projects/clients/invoices)
        // and set the flag so we don't unnecessarily re-fetch from Supabase.
        if (localStorage.getItem("ametal_pcs_user_data_saved") !== "1") {
          const hasUserData = (parsed.projects?.length > 0) ||
                              (parsed.clients?.length > 0) ||
                              (parsed.vendors?.length > 0) ||
                              (parsed.clientInvoices?.length > 0) ||
                              (parsed.vendorInvoices?.length > 0) ||
                              (parsed.companyAccounts?.length > 0);
          if (hasUserData) {
            try { localStorage.setItem("ametal_pcs_user_data_saved", "1"); } catch {}
          }
        }
        return merged;
      }
    } catch {}
    return { ...SEED_DATA };
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const saveTimer = useRef(null);
  const prevDataRef = useRef(null);
  const companyIdRef = useRef(null);
  const skipNextSave = useRef(false);
  const realtimeActive = useRef(false);

  // ── Auth listener ──
  useEffect(() => {
    // Timeout getSession to avoid hanging when Supabase is unreachable
    const sessionTimeout = setTimeout(() => {
      setSession(prev => prev === undefined ? null : prev);
    }, 1500);
    supabase.auth.getSession()
      .then(({ data: { session: s } }) => {
        clearTimeout(sessionTimeout);
        // Don't overwrite a local session (restored from trust token) with null
        setSession(prev => (!s && prev?.local) ? prev : (s || null));
      })
      .catch(() => {
        clearTimeout(sessionTimeout);
        setSession(prev => prev?.local ? prev : null);
      });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      // Don't overwrite a local session with null from Supabase auth changes
      if (!s) {
        setSession(prev => {
          if (prev?.local) return prev;
          setProfile(null);
          setLoading(false);
          unsubscribeAll();
          realtimeActive.current = false;
          return null;
        });
      } else {
        setSession(s);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load data when session changes ──
  useEffect(() => {
    if (session === undefined) return;
    if (!session) { setLoading(false); return; }
    // Local auth — localStorage is the source of truth.
    // Only fetch from Supabase if localStorage has no data (fresh browser).
    // This prevents the race condition where async Supabase load overwrites
    // records the user created while the load was in progress.
    if (session.local) {
      // Only trust localStorage if THIS browser has actually saved user data before.
      // Seed data has companies.length > 0 too, so we can't use that to distinguish
      // "fresh browser (should fetch from Supabase)" from "returning user (use localStorage)".
      // The flag is set in the debounced save effect once the user has made real changes.
      const hasLocalData = localStorage.getItem("ametal_pcs_user_data_saved") === "1";
      if (hasLocalData) {
        // Already have data from localStorage — merge in server-only records
        // (e.g. photos/docs added via Telegram bot directly to DB)
        if (isSupabaseEnabled) {
          (async () => {
            try {
              const serverData = await Promise.race([
                loadAllFromSupabase(),
                new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 10000)),
              ]).catch(() => null);
              if (serverData) {
                // Merge: for each key, combine local + server records by primary key
                setData(prev => {
                  const merged = { ...prev };
                  const SERVER_KEYS = ["sitePhotos", "documents", "drawings", "rfis", "submittals", "correspondence", "quotations", "datasheets", "otherDocs", "workPermitDocs", "qcReportDocs"];
                  for (const key of SERVER_KEYS) {
                    if (serverData[key]?.length) {
                      const pkField = PK_MAP[key] || "id";
                      const localArr = prev[key] || [];
                      const localIds = new Set(localArr.map(r => r[pkField]));
                      const newRecords = serverData[key].filter(r => !localIds.has(r[pkField]));
                      if (newRecords.length) {
                        merged[key] = [...localArr, ...newRecords];
                      }
                    }
                  }
                  return merged;
                });
              }
            } catch (e) {
              console.warn("Background merge failed:", e);
            }
          })();
        }
        setLoading(false);
        return;
      }
      (async () => {
        try {
          if (isSupabaseEnabled) {
            const tableData = await Promise.race([
              loadAllFromSupabase(),
              new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 15000)),
            ]).catch(() => null);
            if (tableData) {
              const hasData = tableData.companies && tableData.companies.length > 0;
              if (hasData) {
                const seed = SEED_DATA;
                const merged = { ...seed, ...tableData };
                Object.keys(seed).forEach(key => {
                  if (!merged[key] || !Array.isArray(merged[key])) merged[key] = seed[key];
                });
                skipNextSave.current = true;
                prevDataRef.current = merged;
                setData(merged);
                try {
                  localStorage.setItem("ametal_pcs_data", JSON.stringify(merged));
                  localStorage.setItem("ametal_pcs_version", String(DATA_VERSION));
                  // Mark this browser as having valid data so future loads use
                  // the fast localStorage + background-merge path instead of
                  // re-running the full Supabase fetch on every refresh.
                  localStorage.setItem("ametal_pcs_user_data_saved", "1");
                } catch {}
              }
            }
          }
        } catch (e) {
          console.warn("Local session Supabase load failed, using localStorage:", e);
        }
        setLoading(false);
      })();
      return;
    }

    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        // Fetch profile
        const { data: prof, error: profErr } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single();

        if (profErr) throw profErr;
        if (cancelled) return;
        setProfile(prof);

        if (!prof?.company_id) {
          throw new Error("Profile is missing a company_id");
        }
        companyIdRef.current = prof.company_id;

        // Load all tables from Supabase
        if (isSupabaseEnabled) {
          try {
            const tableData = await Promise.race([
              loadAllFromSupabase(),
              new Promise((_, rej) => setTimeout(() => rej(new Error("Load timeout")), 15000)),
            ]);
            if (tableData && !cancelled) {
              const hasData = tableData.companies && tableData.companies.length > 0;
              if (hasData) {
                const seed = SEED_DATA;
                const merged = { ...seed, ...tableData };
                Object.keys(seed).forEach(key => {
                  if (!merged[key] || !Array.isArray(merged[key])) merged[key] = seed[key];
                });
                skipNextSave.current = true;
                prevDataRef.current = merged;
                setData(merged);
                try {
                  localStorage.setItem("ametal_pcs_data", JSON.stringify(merged));
                  localStorage.setItem("ametal_pcs_version", String(DATA_VERSION));
                } catch {}
                if (!cancelled) setLoading(false);
                return;
              }
            }
          } catch (e) {
            console.warn("Individual tables load failed, falling back to app_data:", e);
          }
        }

        // Fallback: load from app_data JSONB blob
        const { data: appRow, error: appErr } = await supabase
          .from("app_data")
          .select("data, data_version")
          .eq("company_id", prof.company_id)
          .single();

        if (cancelled) return;

        if (appErr && appErr.code === "PGRST116") {
          const seed = SEED_DATA;
          await supabase.from("app_data")
            .insert({ company_id: prof.company_id, data: seed, data_version: DATA_VERSION });
          skipNextSave.current = true;
          prevDataRef.current = seed;
          setData(seed);
          try {
            localStorage.setItem("ametal_pcs_data", JSON.stringify(seed));
            localStorage.setItem("ametal_pcs_version", String(DATA_VERSION));
          } catch {}
        } else if (appErr) {
          throw appErr;
        } else {
          const seed = SEED_DATA;
          const merged = { ...seed };
          if (appRow.data) {
            Object.keys(seed).forEach(key => {
              if (appRow.data[key] !== undefined) merged[key] = appRow.data[key];
            });
          }
          skipNextSave.current = true;
          prevDataRef.current = merged;
          setData(merged);
          try {
            localStorage.setItem("ametal_pcs_data", JSON.stringify(merged));
            localStorage.setItem("ametal_pcs_version", String(DATA_VERSION));
          } catch {}
        }
      } catch (err) {
        const msg = err?.message || String(err);
        console.error("Failed to load data:", msg);
        if (cancelled) return;
        setError(msg);
        // Fallback: localStorage
        try {
          const saved = localStorage.getItem("ametal_pcs_data");
          if (saved) {
            const parsed = JSON.parse(saved);
            const seed = SEED_DATA;
            const merged = { ...seed };
            Object.keys(seed).forEach(key => {
              if (parsed[key] !== undefined) merged[key] = parsed[key];
            });
            skipNextSave.current = true;
            prevDataRef.current = merged;
            setData(merged);
          } else {
            const seed = SEED_DATA;
            skipNextSave.current = true;
            prevDataRef.current = seed;
            setData(seed);
          }
        } catch {
          const seed = SEED_DATA;
          skipNextSave.current = true;
          prevDataRef.current = seed;
          setData(seed);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [session]);

  // ── Real-time subscriptions ──
  useEffect(() => {
    if (!session || session.local || !isSupabaseEnabled || realtimeActive.current) return;

    realtimeActive.current = true;

    subscribeToChanges((tableName, eventType, record) => {
      const jsKey = TABLE_TO_KEY[tableName];
      if (!jsKey) return;
      const pk = PK_MAP[jsKey] || "id";

      setData(prev => {
        if (!prev) return prev;
        const arr = prev[jsKey] || [];
        let updated;

        if (eventType === "INSERT") {
          // Only add if not already present
          if (arr.some(r => r[pk] === record[pk])) return prev;
          updated = [...arr, record];
        } else if (eventType === "UPDATE") {
          const idx = arr.findIndex(r => r[pk] === record[pk]);
          if (idx === -1) {
            updated = [...arr, record];
          } else {
            updated = arr.map((r, i) => i === idx ? { ...r, ...record } : r);
          }
        } else if (eventType === "DELETE") {
          updated = arr.filter(r => r[pk] !== record[pk]);
        } else {
          return prev;
        }

        const newData = { ...prev, [jsKey]: updated };
        // Update prevDataRef so we don't re-sync this change back
        prevDataRef.current = newData;
        return newData;
      });
    });

    return () => {
      unsubscribeAll();
      realtimeActive.current = false;
    };
  }, [session]);

  // ── Debounced save on data changes ──
  useEffect(() => {
    if (!data) return;
    if (skipNextSave.current) { skipNextSave.current = false; return; }

    // Always save to localStorage immediately on every data change.
    // Strip large binary fields (attachment_data) to avoid exceeding localStorage quota (~5MB).
    const stripped = { ...data };
    const STRIP_KEYS = ["drawings", "rfis", "submittals", "correspondence", "documents"];
    for (const key of STRIP_KEYS) {
      if (Array.isArray(stripped[key])) {
        stripped[key] = stripped[key].map(r => {
          if (!r.attachment_data) return r;
          const { attachment_data, ...rest } = r;
          return rest;
        });
      }
    }
    try {
      localStorage.setItem("ametal_pcs_data", JSON.stringify(stripped));
      localStorage.setItem("ametal_pcs_version", String(DATA_VERSION));
      // Mark this browser as having user-modified data so future logins trust localStorage
      // (as opposed to doing a full Supabase reload — see session.local handler).
      localStorage.setItem("ametal_pcs_user_data_saved", "1");
    } catch (e) { console.warn("localStorage save failed:", e); }

    // Debounced sync to Supabase (only when session exists)
    if (session) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {

        const oldData = prevDataRef.current;
        if (!oldData) {
          prevDataRef.current = data;
          return;
        }

        try {
          await syncToSupabase(oldData, data);
          prevDataRef.current = data;
        } catch (err) {
          console.error("Supabase sync error:", err);
          // Fallback: save to app_data blob
          if (companyIdRef.current) {
            try {
              await supabase.from("app_data")
                .update({ data })
                .eq("company_id", companyIdRef.current);
            } catch {}
          }
        }
      }, SAVE_DEBOUNCE_MS);
    }

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [data, session]);

  // ── Save to localStorage on page unload (prevent data loss on refresh) ──
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        if (data) {
          const stripped = { ...data };
          const STRIP_KEYS = ["drawings", "rfis", "submittals", "correspondence", "documents"];
          for (const key of STRIP_KEYS) {
            if (Array.isArray(stripped[key])) {
              stripped[key] = stripped[key].map(r => {
                if (!r.attachment_data) return r;
                const { attachment_data, ...rest } = r;
                return rest;
              });
            }
          }
          localStorage.setItem("ametal_pcs_data", JSON.stringify(stripped));
          localStorage.setItem("ametal_pcs_version", String(DATA_VERSION));
        }
      } catch {}
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [data]);

  // ── Local auth fallback ──
  const loginLocal = useCallback((email) => {
    const seed = SEED_DATA;
    let appData;
    try {
      const saved = localStorage.getItem("ametal_pcs_data");
      const savedVer = Number(localStorage.getItem("ametal_pcs_version") || "0");
      if (saved && savedVer >= DATA_VERSION) {
        const parsed = JSON.parse(saved);
        const merged = { ...seed };
        Object.keys(seed).forEach(key => {
          if (parsed[key] !== undefined) merged[key] = parsed[key];
        });
        appData = merged;
      } else {
        appData = seed;
      }
    } catch {
      appData = seed;
    }
    // Set company_id for Supabase sync (use first company from data)
    const company = appData.companies?.[0];
    if (company) companyIdRef.current = company.company_id;
    skipNextSave.current = true;
    prevDataRef.current = appData;
    setData(appData);
    setSession({ user: { email }, local: true });
    setLoading(false);
    try {
      localStorage.setItem("ametal_pcs_data", JSON.stringify(appData));
      localStorage.setItem("ametal_pcs_version", String(DATA_VERSION));
    } catch {}
  }, []);

  // ── Sign out ──
  const signOut = useCallback(async () => {
    unsubscribeAll();
    realtimeActive.current = false;
    try { await supabase.auth.signOut(); } catch {}
    setSession(null);
    setProfile(null);
    // Restore seed data so login screen has access to users list
    const seed = SEED_DATA;
    try {
      const saved = localStorage.getItem("ametal_pcs_data");
      const savedVer = Number(localStorage.getItem("ametal_pcs_version") || "0");
      if (saved && savedVer >= DATA_VERSION) {
        const parsed = JSON.parse(saved);
        const merged = { ...seed };
        Object.keys(seed).forEach(key => {
          if (parsed[key] !== undefined) merged[key] = parsed[key];
        });
        setData(merged);
        prevDataRef.current = merged;
        return;
      }
    } catch {}
    setData({ ...seed });
    prevDataRef.current = { ...seed };
  }, []);

  const value = {
    data, setData, session, profile, loading, error, signOut, loginLocal,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
